import { forwardRef } from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

export interface CheckboxOption {
  label: string;
  value: string;
}

interface CheckboxGroupProps {
  name: string;
  control: Control<any>;
  errors: FieldErrors;
  options: CheckboxOption[];
  label?: string;
  error?: string;
  className?: string;
}

const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
  ({ name, control, errors, options, label, error, className }, ref) => {
    const fieldError = errors[name]?.message as string | undefined;

    return (
      <div ref={ref} className={cn("space-y-3", className)}>
        {label && (
          <Label className="block text-sm font-medium mb-4">{label}</Label>
        )}
        <Controller
          name={name}
          control={control}
          defaultValue={[]}
          render={({ field }) => {
            const selectedValues = Array.isArray(field.value)
              ? field.value
              : [];

            const handleChange = (
              optionValue: string,
              checked: boolean | string
            ) => {
              const isChecked = checked === true || checked === "checked";
              const newValues = isChecked
                ? [...selectedValues, optionValue]
                : selectedValues.filter((v: string) => v !== optionValue);
              field.onChange(newValues);
            };

            return (
              <div className="space-y-3">
                {options.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`${name}-${option.value}`}
                      checked={selectedValues.includes(option.value)}
                      onCheckedChange={(checked) =>
                        handleChange(option.value, checked === true)
                      }
                      checkedClassName="bg-prime-800"
                    />
                    <Label
                      htmlFor={`${name}-${option.value}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            );
          }}
        />
        {(error || fieldError) && (
          <p className="text-red-500 text-xs mt-1">{error || fieldError}</p>
        )}
      </div>
    );
  }
);

CheckboxGroup.displayName = "CheckboxGroup";

export default CheckboxGroup;
